import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@/lib/utils";

// Side effect only: disables svg-core's runtime CSS injection. Lives here
// because Icon.tsx is the one module present in both graphs. See lib/fontawesome.
import "@/lib/fontawesome";

/**
 * An icon mirrors in RTL iff its glyph encodes a reading direction. Keyed by
 * `IconDefinition.iconName`, so this stays correct if a call site ever reaches
 * for a different style of the same icon.
 *
 * Any name carrying a `left`/`right` segment mirrors (arrows, chevrons,
 * angles, carets, brackets), EXCEPT circular motion: `rotate-right`,
 * `clock-rotate-left` read the same either way and mirroring makes "refresh"
 * spin backwards. A hand-list here missed `arrow-right-to-bracket` for months,
 * which is why it is a rule now. Vertical arrows, media transport, checkmarks
 * and anything containing a letter or digit do not mirror.
 */
const MIRROR_SEGMENT = /(?:^|-)(?:left|right)(?:-|$)/;
/** Directional glyphs whose names carry no left/right segment. */
const AUTO_MIRROR: ReadonlySet<string> = new Set([
	"reply",
	"share",
	"share-nodes",
	"sidebar",
	"sidebar-flip",
	"arrow-progress",
	"indent",
	"outdent",
]);

function mirrorsInRtl(iconName: string): boolean {
	return (
		AUTO_MIRROR.has(iconName) || (MIRROR_SEGMENT.test(iconName) && !iconName.includes("rotate"))
	);
}

export type IconMirror = "auto" | "always" | "never";

export interface IconProps {
	/** An imported icon definition, e.g. `faTrashCan`. Never a string. */
	icon: IconDefinition;
	/**
	 * Square size in px. Omit to inherit — the icon then falls back to 1em, or
	 * to whatever `size-*` utility the parent applies (shadcn's `[&_svg]:size-4`).
	 */
	size?: number;
	className?: string;
	/**
	 * `auto` (default) mirrors only icons listed in AUTO_MIRROR; `always` forces
	 * it for a directional icon we haven't listed; `never` opts one out.
	 *
	 * Emits the static Tailwind class `rtl:-scale-x-100`, resolved by the `dir`
	 * attribute the root layout sets — no `useLocale()`, so this is safe in RSC.
	 */
	mirror?: IconMirror;
	/**
	 * Accessible name. Omit for decorative icons (the default) — they render
	 * `aria-hidden`. Pass this only when the icon is the sole content of an
	 * interactive element that has no adjacent text.
	 */
	label?: string;
	/** Continuous spin, for busy states. */
	spin?: boolean;
	/** CSSProperties, plus FA's own `--fa-*` custom properties. */
	style?: FontAwesomeIconProps["style"];
}

/**
 * The single icon primitive for the app — FontAwesome Pro 7, Classic Solid.
 *
 *   <Icon icon={faTrashCan} size={14} />
 *   <Icon icon={faArrowLeft} size={16} />          // mirrors in Arabic
 *   <Icon icon={faXmark} label={t("close")} />     // sole content of a button
 *   <Icon icon={faSpinner} spin size={14} />
 */
export function Icon({ icon, size, className, mirror = "auto", label, spin, style }: IconProps) {
	const flipped = mirror === "always" || (mirror === "auto" && mirrorsInRtl(icon.iconName));

	return (
		<FontAwesomeIcon
			icon={icon}
			// Inline width/height beats any NORMAL class, so sized call sites stay
			// immune to cascade-layer ordering. Unsized ones inherit 1em / a parent
			// `size-*`.
			//
			// ⚠️ It does NOT beat an `!important` utility: an important author
			// declaration outranks a normal inline one. `ui/badge.tsx:9` carries
			// `[&>svg]:size-3!`, so an `<Icon size={14}>` inside a `Badge` renders at
			// 12px and the `size` prop is silently ignored. This docblock claimed
			// immunity "to any class" until TRY-138's adversary pass found the
			// counter-example.
			style={size === undefined ? style : { width: size, height: size, ...style }}
			// FA7 is fixed-width (--fa-width) by default; Jana's icons are square.
			widthAuto
			spin={spin}
			className={cn("shrink-0 align-[-0.125em]", flipped && "rtl:-scale-x-100", className)}
			aria-hidden={label ? undefined : true}
			role={label ? "img" : undefined}
			aria-label={label}
		/>
	);
}
